import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Avatar from 'react-avatar'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { TApi, TRoles } from '../../../../shared/const'
import http from '../../../../shared/http'
import { stateLabel, SUPPORTERS_COMPANY_STORAGE_KEY as COMPANY_STORAGE_KEY } from '../../../../shared/helpers'
import { notifyError } from '../../../../shared/errorMessage'
import useScrollTopOnTabChange from '../../../../shared/useScrollTopOnTabChange'
import useDebouncedValue from '../../../../shared/useDebouncedValue'
import useLatestOnly from '../../../../shared/useLatestOnly'

import Btn from '../../../../UI/Btn'
import BtnFilter from '../../../../UI/BtnFilter'
import ViewToggle from '../../../../UI/ViewToggle'
import FilterPanel from '../../../../UI/FilterPanel'
import useFilters from '../../../../UI/FilterPanel/useFilters'
import {
	competenceField,
	industryField,
	matchingField,
	schoolTypeField,
	stateField
} from '../../../../UI/FilterPanel/fields'
import MiniHeader from '../../../../UI/MiniHeader'
import Pagination from '../../../../UI/Pagination'
import Search from '../../../../UI/Search'
import ReminderNote from '../../../../UI/ReminderNote'
import SupporterDetailModal from './SupporterDetailModal'
import InviteExpertModal from './InviteExpertModal'
import PendingInvitations from './PendingInvitations'
import CompanyExperts from '../../student/CompanyExperts'

import styles from './style.module.scss'
import AuthAvatar from '../../../../UI/AuthAvatar'

const LIMIT = 12

/*
 * Zwei Reiter, nicht drei.
 *
 * Bis 09.08.2026 stand zwischen "Unsere" und "Alle" ein Reiter "Passende". Er
 * zeigte eine Teilmenge von "Alle" — anfragen liess sich in beiden Listen
 * dasselbe. Wer die kleinere Liste "passende" nennt und in der groesseren
 * denselben Knopf anbietet, muss erklaeren, warum. Jetzt gibt es **eine**
 * Liste: Die Passenden stehen oben (der Server sortiert danach), tragen ein
 * Zeichen, und wer nur sie sehen will, setzt den Filter "nur passende".
 */
const TABS = [
	{ key: 'connected', label: 'ourExperts' },
	{ key: 'all', label: 'allExperts' }
]

/*
 * Vierter Reiter: die selbst erfassten Wirtschaftsexpert:innen — nur noch
 * Altbestand.
 *
 * Ueber dieses Formular trug eine Schueler:in einen vollstaendigen
 * Personendatensatz ueber jemand anderen ein, bis hin zu Geburtsdatum und
 * Anschrift, ohne dass die genannte Person davon wusste. Angelegt wird seit
 * 09.08.2026 nichts mehr (an dieser Stelle steht "Expert:in einladen"), und was
 * niemand bestaetigt hat, loescht der naechtliche Lauf nach seiner Frist.
 *
 * Der Reiter bleibt, solange es Eintraege gibt: Die Junior Company soll sehen,
 * was ueber wen gespeichert ist, bis wann es noch steht und wie sie die Person
 * richtig einlaedt. Ist die Liste leer, verschwindet er.
 */
const OWN_TAB = { key: 'own', label: 'ownExperts' }

/**
 * Expert:innen-Sicht fuer Lehrkraefte und Schueler:innen.
 *
 * Zeigt ausschliesslich Wirtschaftsexpert:innen - Jury-Mitglieder und
 * Trainer:innen werden laut Spec nur intern von Admins verwaltet. Die Filterung
 * dafuer passiert im Backend, nicht hier.
 */
/*
 * Zeilen oder Kachelraster? Dieselbe Liste, zwei Blickwinkel: Kacheln zeigen
 * Kurzbeschreibung und Kompetenzen und laden zum Stöbern ein, Zeilen bringen
 * viele Namen auf einen Blick. Was zuletzt gewählt war, gilt beim nächsten
 * Aufruf wieder — sonst müsste es jedes Mal neu eingestellt werden.
 */
const VIEW_STORAGE_KEY = 'supporters.view'

export default function SupportersList({ role }) {
	const { t, i18n } = useTranslation()

	// Der Altbestand-Reiter erscheint nur, solange es Altbestand gibt (siehe
	// OWN_TAB). Ein leerer Reiter waere eine Einladung, dort etwas anzulegen —
	// und genau das geht nicht mehr.
	const [ownCount, setOwnCount] = useState(0)
	const tabs = role === TRoles.STUDENT && ownCount > 0 ? [...TABS, OWN_TAB] : TABS

	/*
	 * Anfragen stellen Schüler:innen.
	 *
	 * Die Zusammenarbeit ist die ihrer Junior Company; die Lehrkraft begleitet
	 * sie, tritt aber nicht an ihrer Stelle an eine Expert:in heran. Sie sieht
	 * denselben Bestand, denselben Status und denselben Verlauf — nur den
	 * Anfrage-Knopf nicht. Das Backend prüft nichts dergleichen: Wollte eine
	 * Schule es anders halten, wäre die Anfrage einer Lehrkraft kein Fehler.
	 */
	const canRequest = role === TRoles.STUDENT

	const [tab, setTab] = useState('all')
	const [rows, setRows] = useState([])
	const [count, setCount] = useState(0)
	const [page, setPage] = useState(0)
	const [search, setSearch] = useState('')
	const [isLoading, setLoading] = useState(false)
	const [showFilter, setShowFilter] = useState(false)
	const [lookups, setLookups] = useState({
		industries: [],
		schoolTypes: [],
		competenceGroups: []
	})
	const [detailId, setDetailId] = useState(null)
	const [detailTab, setDetailTab] = useState('profile')
	const [companies, setCompanies] = useState([])
	/*
	 * Fuer welche Junior Company wird gelesen und angefragt?
	 *
	 * Eine Expert:in arbeitet mit einer Junior Company zusammen, nicht mit einer
	 * Lehrkraft. Wer mehrere betreut, muss deshalb sagen koennen, um welche es
	 * gerade geht — sonst heisst "angefragt" an der Kachel nichts, und "passende
	 * Expert:innen" waren die, die zur zufaellig ersten Firma passen.
	 *
	 * Gemerkt wird die Wahl im Browser, nicht am Konto (der Schluessel steht in
	 * shared/helpers, weil die An- und Abmeldung ihn dort wieder wegraeumt). Was
	 * daraus folgen kann, steht unten am Fehlerzweig von `fetchSupporters`.
	 */
	const [companyId, setCompanyId] = useState(() => localStorage.getItem(COMPANY_STORAGE_KEY) || '')
	const [showInvite, setShowInvite] = useState(false)
	// Zählt hoch, wenn eingeladen wurde — die offenen Einladungen laden dann neu.
	const [invitationsVersion, setInvitationsVersion] = useState(0)
	const [view, setView] = useState(() => (localStorage.getItem(VIEW_STORAGE_KEY) === 'rows' ? 'rows' : 'tiles'))

	const tabsRef = useRef(null)
	useScrollTopOnTabChange(tab, tabsRef)

	/*
	 * Dieselbe Leiste wie in allen uebrigen Listen: die Felder kommen aus
	 * FilterPanel/fields, die Ansicht sagt nur, welche sie braucht. Die
	 * Auswahlwerte sind die der Zielgruppen-Felder — sonst stuenden im Filter
	 * Woerter, die auf keinem Profil stehen.
	 */
	const branches = useSelector(state => state.options?.branch) || []
	const fields = useMemo(
		() => [
			// Das erste Feld ist der Ersatz fuer den aufgeloesten Reiter
			// "Passende" — es steht deshalb ganz oben und nicht zwischen den
			// Merkmalen.
			matchingField(t),
			stateField(t),
			industryField(t, lookups.industries, branches, i18n.language),
			schoolTypeField(t, lookups.schoolTypes),
			competenceField(t, lookups.competenceGroups)
		],
		[t, i18n.language, lookups, branches]
	)

	const filters = useFilters({ storageKey: 'supporters-school', fields })
	const filterQuery = JSON.stringify(filters.query)
	const onlyMatching = (filters.values?.matching || []).includes('yes')
	const debouncedSearch = useDebouncedValue(search)
	const latest = useLatestOnly()

	useEffect(() => {
		http.get(TApi.COMMON_SUPPORTER_LOOKUPS)
			.then(({ data }) => setLookups(data || {}))
			.catch(() => {}) // Meldung kommt aus shared/http
	}, [])

	/*
	 * Gibt es noch selbst erfasste Alteintraege? Nur dann steht der vierte
	 * Reiter da. Gezaehlt wird beim Aufruf und nach jedem Loeschen — faellt der
	 * letzte Eintrag weg, verschwindet der Reiter mit ihm.
	 */
	const countOwnExperts = useCallback(() => {
		if (role !== TRoles.STUDENT) return

		http.get(TApi.STUDENT_EXPERTS, { params: { limit: 1, offset: 0 }, disableToast: true })
			.then(({ data }) => setOwnCount(data?.count || 0))
			.catch(() => setOwnCount(0))
	}, [role])

	useEffect(() => {
		countOwnExperts()
	}, [countOwnExperts])

	const selectedCompany = companies.find(item => String(item.id) === String(companyId))
	const showCompanyChoice = companies.length > 1

	/*
	 * Die Wahl gilt beim naechsten Aufruf weiter — Zustand und Browser gehoeren
	 * deshalb zusammen gesetzt. Wurde nur der Zustand geaendert, stand nach dem
	 * Neuladen wieder die alte Firma im Aufruf.
	 */
	const rememberCompany = useCallback(value => {
		setCompanyId(value)
		if (value) localStorage.setItem(COMPANY_STORAGE_KEY, value)
		else localStorage.removeItem(COMPANY_STORAGE_KEY)
	}, [])

	const fetchSupporters = useCallback(() => {
		// Der Reiter mit den selbst erfassten Eintraegen holt seine Daten selbst
		// (Schueler:innen-Route). Die Unterstuetzer:innen-Schnittstelle kennt diesen
		// Reiter nicht und antwortete mit 400 samt Fehlermeldung im Bild.
		if (tab === OWN_TAB.key) return

		// Antworten koennen einander ueberholen - es zaehlt die zuletzt gestellte.
		const isCurrent = latest()

		setLoading(true)
		http.get(TApi.COMMON_SUPPORTERS, {
			params: {
				tab,
				limit: LIMIT,
				offset: page * LIMIT,
				search: debouncedSearch,
				filter: filterQuery,
				...(companyId ? { companyId } : {})
			},
			// `disableToast`, weil ein Fall hier keine Meldung sein soll (siehe
			// unten). Sonst käme sie aus shared/http, bevor dieser Zweig greift.
			disableToast: true
		})
			.then(({ data }) => {
				if (!isCurrent()) return
				setRows(data.rows || [])
				setCount(data.count || 0)
				setCompanies(data.companies || [])

				// Der Server sagt, aus wessen Sicht er geantwortet hat. Beim ersten
				// Aufruf (und wenn eine gemerkte Firma nicht mehr betreut wird)
				// uebernimmt die Ansicht das, statt stumm eine andere zu zeigen.
				const known = (data.companies || []).some(item => String(item.id) === String(companyId))
				if (!known) rememberCompany(data.companyId ? String(data.companyId) : '')
			})
			.catch(error => {
				if (!isCurrent()) return

				/*
				 * Die gemerkte Junior Company gehört jemand anderem.
				 *
				 * Gemerkt wird im Browser, nicht am Konto: Wer sich am selben
				 * Rechner nacheinander als zwei Personen anmeldet, schickte die
				 * Firma der Vorgängerin mit. Der Server weist das mit 403 ab —
				 * richtig so —, nur blieb die Ansicht danach dauerhaft bei „0
				 * Wirtschaftsexperten" stehen: Die Auswahl wurde nie zurückgesetzt,
				 * und ohne Liste gab es auch kein Auswahlfeld, um sie zu ändern.
				 * Also verwerfen und ohne sie erneut lesen — eine Fehlermeldung
				 * hilft hier niemandem weiter.
				 */
				if (error?.response?.status === 403 && companyId) {
					rememberCompany('')
					return
				}

				notifyError(error)
			})
			.finally(() => {
				if (isCurrent()) setLoading(false)
			})
		// t bewusst NICHT in den Abhaengigkeiten: useTranslation liefert bei
		// jedem Render eine neue Funktionsreferenz. Mit t in der Liste entstuende
		// eine Endlosschleife Render -> Fetch -> Render.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tab, page, debouncedSearch, filterQuery, companyId])

	useEffect(() => {
		fetchSupporters()
	}, [fetchSupporters])

	const handleChangeCompany = value => {
		setPage(0)
		rememberCompany(value)
	}

	/*
	 * Der Knopf an der Kachel schickt die Anfrage nicht mehr selbst ab: Seit
	 * Anforderung #232 sagt jede Anfrage, wobei die Junior Company Unterstützung
	 * sucht. Er öffnet deshalb das Profil gleich im Reiter „Kontakt", wo das
	 * Feld und — bei mehreren Firmen — die Auswahl stehen.
	 */
	const requestSupporter = id => {
		setDetailTab('contact')
		setDetailId(id)
	}

	const openDetail = id => {
		setDetailTab('profile')
		setDetailId(id)
	}

	const changeView = next => {
		setView(next)
		localStorage.setItem(VIEW_STORAGE_KEY, next)
	}

	/*
	 * Die drei Bausteine, die Kachel und Zeile gemeinsam haben. Zwei Abschriften
	 * derselben Darstellung liefen nach der ersten Änderung auseinander — dass
	 * eine Expert:in in der Zeile anders heißt als in der Kachel, wäre ein
	 * Fehler, den niemand sucht.
	 */

	/* Name und Firma öffnen das Detailprofil - bewusst nicht die ganze Fläche:
	   Der Anfrage-Knopf soll direkt bedienbar bleiben, ohne das Fenster zu
	   öffnen. */
	const identity = (supporter, size) => (
		<header
			className={clsx(styles.cardHeader, styles.cardHeaderClickable)}
			onClick={() => openDetail(supporter.id)}
			role="button"
			tabIndex={0}
			onKeyDown={e => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault()
					openDetail(supporter.id)
				}
			}}
		>
			<AuthAvatar
				hashName={supporter.avatarHashName}
				className={clsx(styles.avatar, size === 'small' && styles.avatarSmall)}
				fallback={<Avatar name={supporter.displayName || '?'} size={size === 'small' ? '32' : '44'} round />}
			/>
			<div className={styles.cardTitle}>
				<span className={styles.name}>{supporter.displayName}</span>
				<span className={styles.company}>{supporter.company}</span>
			</div>
		</header>
	)

	/*
	 * Woran es an dieser Junior Company scheitert. Die Anfrage bleibt trotzdem
	 * möglich — eine Zielgruppe ist eine Vorliebe, kein Verbot, und die bekannte
	 * Tante zählt mehr als jedes Kriterium. Aber blind soll niemand anfragen und
	 * sich über die Absage wundern.
	 *
	 * **Nicht mehr im eigenen Bestand.** Der Hinweis beantwortet die Frage „Soll
	 * ich diese Person anfragen?"; bei einer verbundenen oder angefragten
	 * Person ist sie entschieden. Dort stand damit eine Warnung, die nichts mehr
	 * ändern konnte und die laufende Zusammenarbeit schlechtredete. Was dort
	 * zählt, steht jetzt daneben: **wobei** die Person unterstützen kann
	 * (helpNote).
	 */
	const mismatchNote = supporter =>
		!supporter.connectionStatus &&
		Boolean(supporter.matchMisses?.length) && (
			<p className={styles.mismatch}>
				{t('doesNotMatch', {
					criteria: supporter.matchMisses.map(key => t(`matchCriterion${key}`)).join(', ')
				})}
			</p>
		)

	/*
	 * Dasselbe in positiv — und nur dort, wo es eine Entscheidung stützt. Es
	 * ersetzt den aufgelösten Reiter „Passende": Nach ihm sortiert der Server,
	 * dieses Zeichen sagt, warum die Person oben steht.
	 */
	const matchNote = supporter =>
		!supporter.connectionStatus &&
		supporter.matchesCompany && <p className={styles.match}>{t('matchesYourCompany')}</p>

	/*
	 * Im eigenen Bestand: wobei kann die Person unterstützen und wie ist sie zu
	 * erreichen? Die Kompetenzen stehen ohnehin auf der Kachel; hier kommt der
	 * Kontaktweg dazu, sobald die Anfrage angenommen ist — vorher gibt es keinen
	 * (die Kontaktdaten hält der Server bis dahin zurück).
	 */
	const helpNote = supporter => {
		if (!supporter.connectionStatus) return null

		const wege = [...(supporter.contactModes || []), ...(supporter.contactTimes || [])]
		if (!wege.length) return null

		return <p className={styles.help}>{t('reachableVia', { modes: wege.join(', ') })}</p>
	}

	/*
	 * Besteht schon eine Verbindung, steht ihr Stand da; sonst der Weg zur
	 * Anfrage — und den gehen Schüler:innen (siehe canRequest). Lehrkräfte sehen
	 * an dieser Stelle nichts, nicht einen gesperrten Knopf: Ein Knopf, der nie
	 * gedrückt werden kann, ist eine Frage ohne Antwort.
	 *
	 * Aus demselben Grund steht bei „nimmt derzeit keine neuen Junior Companies
	 * an" ein Satz statt eines Knopfes. Bis 09.08.2026 war der Knopf dort nur
	 * ausgegraut — ohne Begründung, und die Anfrage hätte der Server ohnehin mit
	 * einer Fehlermeldung abgewiesen.
	 */
	const statusOrRequest = supporter => {
		if (supporter.connectionStatus)
			return (
				<span className={clsx(styles.connection, styles[`connection${supporter.connectionStatus}`])}>
					{t(`connectionStatus${supporter.connectionStatus}`)}
				</span>
			)

		if (supporter.openForNewCompanies === false)
			return <span className={styles.closed}>{t('notOpenForNewCompanies')}</span>

		if (!canRequest) return null

		return (
			<Btn
				theme="secondary"
				size="small"
				className={styles.requestBtn}
				onClick={() => requestSupporter(supporter.id)}
			>
				{t('requestExpert')}
			</Btn>
		)
	}

	return (
		<div className={styles.root}>
			<MiniHeader className={styles.header} title={t('Economic experts')} bg="#f8faff">
				<div className={styles.headerContent}>
					{/*
					 * Wer niemanden Passendes findet, hat oft selbst jemanden: die
					 * Tante mit der Steuerberatung, den Nachbarn aus dem Marketing.
					 * Der Knopf steht deshalb in der Kopfleiste und nicht in einem
					 * Reiter — er ist die Antwort auf ein leeres Suchergebnis.
					 *
					 * Links von der Suche: Ganz rechts steht in jeder Ansicht der
					 * Filter, davor die Suche. Eine Handlung der Seite reiht sich
					 * davor ein, statt die beiden auseinanderzuschieben.
					 */}
					{Boolean(companies.length) && (
						<Btn className={styles.inviteBtn} onClick={() => setShowInvite(true)}>
							{t('inviteExpert')}
						</Btn>
					)}
					<Search
						value={search}
						onChange={e => {
							setPage(0)
							setSearch(e.target.value)
						}}
						placeholder={t('Find a user')}
						onSubmit={e => e.preventDefault()}
					/>
					<BtnFilter className={styles.filterBtn} count={filters.count} onClick={() => setShowFilter(true)}>
						{t('Filter')} ({filters.count})
					</BtnFilter>
				</div>
			</MiniHeader>

			{/*
			 * Zusammengearbeitet wird mit einer Junior Company. Ohne diesen Satz
			 * fragt eine Lehrkraft mit drei betreuten Firmen an, ohne zu wissen,
			 * fuer welche — und liest "unsere" und "passende" aus einer Sicht, die
			 * sie nicht gewaehlt hat.
			 */}
			{Boolean(companies.length) && (
				<div className={styles.companyBar}>
					<span className={styles.companyBarLabel}>{t('requestForCompany')}</span>
					{showCompanyChoice ? (
						<select
							className={styles.companySelect}
							value={companyId}
							onChange={event => handleChangeCompany(event.target.value)}
						>
							{companies.map(item => (
								<option key={item.id} value={item.id}>
									{item.name}
								</option>
							))}
						</select>
					) : (
						<strong className={styles.companyBarName}>{companies[0].name}</strong>
					)}
					<span className={styles.companyBarHint}>{t('supporterCompanyScopeHint')}</span>
				</div>
			)}

			{/*
			 * Warum an den Kacheln kein Anfrage-Knopf steht: Anfragen stellen die
			 * Schueler:innen (Anforderung #93). Der Satz steht einmal hier und nicht
			 * an jeder Kachel — sonst stuende an jeder Karte eine Absage. Ohne ihn
			 * sieht die Lehrkraft nur, dass etwas fehlt, und haelt es fuer einen
			 * Fehler. Bewusst ausserhalb der Firmenleiste: Die gibt es nur mit
			 * betreuter Junior Company, die Frage stellt sich aber auch ohne.
			 */}
			{!canRequest && <p className={styles.roleHint}>{t('supporterRequestByStudentsHint')}</p>}

			{/*
			 * Offene Einladungen der gewählten Junior Company (#248). Ohne diesen
			 * Block war eine Einladung bis zur Annahme unsichtbar — wer nachsehen
			 * wollte, lud ein zweites Mal ein und bekam eine Absage.
			 */}
			{Boolean(companies.length) && (
				<PendingInvitations companyId={selectedCompany?.id || companyId} refreshKey={invitationsVersion} />
			)}

			<div className={styles.tabs} ref={tabsRef}>
				{tabs.map(item => (
					<button
						key={item.key}
						type="button"
						className={clsx(styles.tab, tab === item.key && styles.tabActive)}
						onClick={() => {
							setPage(0)
							setTab(item.key)
						}}
					>
						{t(item.label)}
					</button>
				))}
			</div>

			{tab === OWN_TAB.key ? (
				<CompanyExperts embedded onChanged={countOwnExperts} />
			) : (
				<>
					<div className={styles.subHeader}>
						<Pagination
							limit={LIMIT}
							page={page}
							label={t('Economic experts')}
							countAll={count}
							onChange={setPage}
						/>

						{/*
						 * Kurzwahl auf dasselbe Filterfeld (siehe fields.js) — sie
						 * hat keinen eigenen Zustand daneben, sondern liest und
						 * setzt „matching". Nur in der grossen Liste: Im eigenen
						 * Bestand gibt es nichts einzuschraenken.
						 */}
						{tab === 'all' && (
							<button
								type="button"
								className={clsx(styles.matchToggle, onlyMatching && styles.matchToggleOn)}
								aria-pressed={onlyMatching}
								onClick={() => {
									setPage(0)
									filters.setField('matching', onlyMatching ? [] : ['yes'])
								}}
							>
								{t('onlyMatchingExperts')}
							</button>
						)}

						<ViewToggle className={styles.viewToggle} value={view} onChange={changeView} />
					</div>

					{!isLoading && !rows.length ? (
						<div className={styles.empty}>{t('noSupportersFound')}</div>
					) : view === 'rows' ? (
						<div className={styles.rows}>
							{rows.map(supporter => (
								<article key={supporter.id} className={styles.row}>
									<div className={styles.rowMain}>
										{identity(supporter, 'small')}

										{Boolean(supporter.competences?.length) && (
											<div className={clsx(styles.tags, styles.rowTags)}>
												{supporter.competences.slice(0, 4).map(item => (
													<span key={item} className={styles.tag}>
														{item}
													</span>
												))}
											</div>
										)}

										<span className={clsx(styles.state, styles.rowState)}>
											{stateLabel(supporter.state)}
										</span>

										<div className={styles.rowEnd}>{statusOrRequest(supporter)}</div>
									</div>

									{/*
									 * Hinweise stehen unter der Zeile, nicht darin: Sie sind
									 * zwei Sätze lang und würden die Spalten sonst
									 * auseinanderziehen, Zeile für Zeile unterschiedlich weit.
									 */}
									<div className={styles.rowNotes}>
										{matchNote(supporter)}
										{mismatchNote(supporter)}
										{helpNote(supporter)}
										<ReminderNote
											status={supporter.connectionStatus}
											reminderCount={supporter.reminderCount}
											lastReminderAt={supporter.lastReminderAt}
											autoDeclined={supporter.autoDeclined}
										/>
									</div>
								</article>
							))}
						</div>
					) : (
						<div className={styles.grid}>
							{rows.map(supporter => (
								<article key={supporter.id} className={styles.card}>
									{identity(supporter)}

									{matchNote(supporter)}
									{mismatchNote(supporter)}
									{helpNote(supporter)}

									{supporter.bio && <p className={styles.bio}>{supporter.bio}</p>}

									{Boolean(supporter.competences?.length) && (
										<div className={styles.tags}>
											{supporter.competences.slice(0, 4).map(item => (
												<span key={item} className={styles.tag}>
													{item}
												</span>
											))}
										</div>
									)}

									<footer className={styles.cardFooter}>
										<span className={styles.state}>{stateLabel(supporter.state)}</span>
										{statusOrRequest(supporter)}
									</footer>

									{/*
									 * Solange eine Anfrage offen ist, soll sichtbar sein, dass das
									 * System bereits nachfasst - sonst wird hier aus Ungeduld
									 * selbst nachgefragt oder die Anfrage fuer vergessen gehalten.
									 */}
									<ReminderNote
										status={supporter.connectionStatus}
										reminderCount={supporter.reminderCount}
										lastReminderAt={supporter.lastReminderAt}
										autoDeclined={supporter.autoDeclined}
									/>
								</article>
							))}
						</div>
					)}
				</>
			)}

			<SupporterDetailModal
				supporterId={detailId}
				defaultCompanyId={companyId}
				allowRequest={canRequest}
				initialTab={detailTab}
				onClose={() => setDetailId(null)}
				onRequested={() => {
					setDetailId(null)
					fetchSupporters()
				}}
			/>

			{/*
			 * Die Firma steht schon oben in der Leiste — das Fenster fragt sie
			 * nicht ein zweites Mal, sondern lädt für die gewählte ein.
			 */}
			<FilterPanel
				open={showFilter}
				onClose={() => setShowFilter(false)}
				fields={fields}
				values={filters.values}
				count={filters.count}
				onChange={(key, selected) => {
					setPage(0)
					filters.setField(key, selected)
				}}
				onResetAll={() => {
					setPage(0)
					filters.resetAll()
				}}
			/>

			<InviteExpertModal
				show={showInvite}
				companies={selectedCompany ? [selectedCompany] : companies}
				onClose={() => setShowInvite(false)}
				onInvited={() => {
					setShowInvite(false)
					setInvitationsVersion(version => version + 1)
					fetchSupporters()
				}}
			/>
		</div>
	)
}

SupportersList.propTypes = {
	role: PropTypes.string
}
